import {
  type FC,
  forwardRef,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import {
  addMissingRelationshipToHierarchy,
  buildRelationshipHierarchy,
  ComponentType,
  type FormkitNode,
  NodeType,
  type RelationshipHierarchy,
} from "@hightouch/formkit";
import {
  Box,
  Column,
  Row,
  Spinner,
  Switch,
  Text,
  useToast,
  TextWithTooltip,
} from "@hightouch/htui";
import { compare } from "fast-json-patch";
import { useFlags } from "launchdarkly-react-client-sdk";
import { get, isEmpty } from "lodash";
import isEqual from "lodash/isEqual";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import type * as Yup from "yup";
import pluralize from "pluralize";

import { JsonEditor } from "src/components/destinations/json-editor";
import { TestSync } from "src/components/destinations/test-sync";
import { DestinationFormProvider } from "src/contexts/destination-form-context";
import { useDraft } from "src/contexts/draft-context";
import { useUser } from "src/contexts/user-context";
import { Form } from "src/formkit/components/form";
import {
  type DraftChange,
  type ExternalSegment,
  type FormkitDestination,
  type FormkitModel,
  FormkitProvider,
  type FormkitSync,
  type FormkitSyncTemplateConfig,
  useFormkitContext,
  useFormkitDataContext,
} from "src/formkit/components/formkit-context";
import { ProcessFormNode } from "src/formkit/formkit";
import { OVERRIDE_CONFIG_FORM_KEY } from "src/formkit/components/sync-template-lock/constants";
import {
  type DestinationDefinitionFragment as DestinationDefinition,
  DraftOperation,
  type FormkitSyncDefinitionQuery,
  type SourceDefinitionFragment as SourceDefinition,
  useDestinationQuery,
  useFormkitSyncDefinitionQuery,
  useFormkitSyncValidationQuery,
  useListSyncableColumnRowKeysQuery,
  useMigrateConfigQuery,
  useSupportsMatchboostingQuery,
  useSyncableColumnsQuery,
} from "src/graphql";
import { useDraftMerger } from "src/hooks/use-draft-merger";
import { useEntitlements } from "src/hooks/use-entitlement";
import * as analytics from "src/lib/analytics";
import { validate as oldValidate } from "src/utils/destinations";

import { PermissionedButton } from "src/components/permission";
import type { ResourcePermissionInput } from "src/components/permission/use-resource-permission";
import CustomDestination from "./forms/custom";
import HeapDestination from "./forms/heap";
import HubspotLegacy from "./forms/hubspot-legacy";
import OrbitForm from "./forms/orbit";
import ReplyioDestination from "./forms/replyio";
import SalesloftDestination from "./forms/salesloft";
import SfmcFileDropDestination from "./forms/sfmc-file-drop";
import TotangoDestination from "./forms/totango";
import type { OverrideConfig } from "./types";
import {
  cleanConfig,
  cleanOverrides,
  getAllChildFields,
  getEmptyValue,
  getUnlockedFields,
  hasEmptyRequiredFields,
  hasUnlockedFields,
  JOURNEY_SETTINGS_CONFIG_KEY,
} from "./utils";
import { getOrphanedConfigKeys } from "./orphaned-config-keys";
import { OrphanedConfigKeysSection } from "./orphaned-config-keys-section";
import { getFieldContext, isNodeVisible } from "./is-node-visible";
import { DocsLink } from "src/components/docs-link";
import { FloatingActionBar } from "src/components/action-bar";
import type {
  JourneyNodeSyncMode,
  JourneySyncExitConfig,
} from "src/types/journeys";
import type { NullableProperties } from "src/types/utils";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import cloneDeep from "lodash/cloneDeep";
import type { WizardSyncConfig } from "src/pages/syncs/create/types";
import { useJourneySyncableColumns } from "src/pages/journeys/hooks/use-journey-syncable-columns";
import {
  mergeJourneySyncModel,
  selectEntryAudienceSyncableColumns,
} from "src/pages/journeys/forms/utils";
import { validateSyncConfigTemplates } from "src/pages/syncs/sync/utils/validate-liquid-v10";
import {
  TemplatePreviewContext,
  type TemplatePreviewField,
} from "src/formkit/components/mapper-v2/template-preview-context";
import { TemplatePreviewModal } from "src/formkit/components/mapper-v2/template-preview-modal";
import { getOverrideConfigIncludingEmptyFields } from "src/utils/sync-templates";
import type { MatchBoosterRegion } from "src/utils/match-booster-regions";

type FormkitDefinition =
  FormkitSyncDefinitionQuery["formkitSyncDefinition"]["form"];

interface DestinationFormContext {
  sync: FormkitSync | undefined;
  matchBoosterRegionControl?: ReactNode;
  selectedMatchBoosterRegion?: MatchBoosterRegion;
  hasDependentSyncRun?: boolean;
  syncConfig: Record<string, unknown> | undefined;
  slug: string | undefined | null;
  model: FormkitModel | undefined;
  isModelDraft: boolean;
  draftChanges: DraftChange[];
  destination: FormkitDestination | undefined;
  // TODO(XXX): get a better type for the destination config
  destinationConfig: any;
  destinationDefinition: DestinationDefinition;
  sourceDefinition: SourceDefinition | undefined;
  externalSegment: ExternalSegment | undefined;
  /**Sync template override specific context */
  isUsingSyncTemplate: boolean;
  supportsOverrides: boolean;
  syncTemplate: FormkitSyncTemplateConfig | undefined;
  hasOverrideConfig: boolean;
  shouldShowFullConfiguration: boolean;
  syncRequiresCompletion: boolean;
  relatedSyncsWithOverrides?: Record<string, number>;
  isSyncForm: boolean; // TEMPORARY. Remove when sync and sync template forms are split
  journeySettings?: {
    mode?: JourneyNodeSyncMode;
    exit_config?: NullableProperties<JourneySyncExitConfig>;
  };
}

/**
 * XXX: Destinations moved to formkit must be removed from here.
 */
const DESTINATION_FORMS = {
  hubspotLegacy: HubspotLegacy,
  totango: TotangoDestination,
  sfmcFileDrop: SfmcFileDropDestination,
  salesloft: SalesloftDestination,
  orbit: OrbitForm,
  custom: CustomDestination,
  replyio: ReplyioDestination,
  heap: HeapDestination,
};

type Props = {
  matchBoosterRegionControl?: ReactNode;
  /**
   * Region chosen in the form but not yet persisted. Region-scoped reads in
   * the mapper prefer it over `sync.matchbooster_region`, which a sync being
   * created does not have yet.
   */
  selectedMatchBoosterRegion?: MatchBoosterRegion;
  /**
   * True when any sync built from the template being edited has run. The
   * template form has no `sync`, so this is the only signal that can pin its
   * region and targeting level.
   */
  hasDependentSyncRun?: boolean;
  journey?: {
    journey_id: string;
    journey_version_id: string;
    entryEventRelationshipId: string | null;
    entryAudienceId?: string | null;
  } | null;
  model?: FormkitModel;
  destination?: FormkitDestination;
  sync?: FormkitSync;
  syncTemplate?: FormkitSyncTemplateConfig;
  syncConfig?: Record<string, unknown> | null;
  destinationDefinition: DestinationDefinition;
  sourceDefinition: SourceDefinition | undefined;
  isDisabled?: boolean;
  /**
   * The overrides saved to syncs created from this sync template
   * The key is the sync id and the value is the number of syncs that have this override
   */
  relatedSyncsWithOverrides?: Record<string, number>;
  /**
   * Whether this is a sync form or a sync template form
   * TODO(samuel): This form should be refactored to be two separate forms
   * and this prop should be removed.
   */
  isSyncForm?: boolean;
  externalSegment?: ExternalSegment;
  slug: string | undefined;
  onSubmit: (config: any) => Promise<void>;
  hideSave?: boolean;
  hideHeader?: boolean;
  disableRowTesting?: boolean;
  /**
   * Override the supportsOverrides calculation - used for templates that require completion
   */
  syncRequiresCompletion?: boolean;
  /**
   * Whether to run validation immediately after form initialization.
   * Defaults to false. Set to true in Journey contexts to show validation errors immediately.
   */
  validateOnMount?: boolean;
  permission:
    | ResourcePermissionInput<"model">
    | ResourcePermissionInput<"sync">;
  testPermission:
    | ResourcePermissionInput<"model">
    | ResourcePermissionInput<"sync">;
  journeySettings?: {
    mode?: JourneyNodeSyncMode;
    exit_config?: NullableProperties<JourneySyncExitConfig>;
  };
  onLogConfigChange?: (
    config: WizardSyncConfig | undefined,
    message: string,
  ) => void;
};

export interface FormRef {
  submit: () => Promise<void>;
  isDirty?: () => boolean;
}

const getRelationshipHierarchy = ({
  supportsOverrides,
  formkitDefinition,
  isUsingSyncTemplate,
}: {
  supportsOverrides: boolean;
  formkitDefinition: FormkitDefinition;
  isUsingSyncTemplate: boolean;
}): RelationshipHierarchy | null => {
  if (!supportsOverrides || !formkitDefinition || !isUsingSyncTemplate) {
    return null;
  }

  return buildRelationshipHierarchy({ node: formkitDefinition });
};

export const DestinationForm = forwardRef<FormRef, Props>(
  function DestinationForm(
    {
      sync,
      matchBoosterRegionControl,
      selectedMatchBoosterRegion,
      hasDependentSyncRun,
      syncTemplate,
      syncConfig,
      relatedSyncsWithOverrides,
      model,
      journey,
      destination,
      destinationDefinition,
      sourceDefinition,
      externalSegment,
      slug,
      onSubmit,
      hideSave,
      permission,
      testPermission,
      journeySettings,
      disableRowTesting = false,
      isSyncForm = true,
      hideHeader = false,
      isDisabled = false,
      syncRequiresCompletion = false,
      validateOnMount = false,
      onLogConfigChange,
    },
    ref,
  ) {
    const { journeysSyncTemplates } = useFlags();
    const { editingDraft, draft } = useDraft();
    const deprecatedForm = slug ? DESTINATION_FORMS[slug] : undefined;
    // Stores the user's explicit preference (via the "Show full configuration"
    // toggle). This is intentionally independent of `syncRequiresCompletion` —
    // incomplete required fields are already surfaced by `isNodeVisible`
    // (see below), so we don't need to force the entire form open just
    // because the template has an empty required field.
    const [showFullConfigPreference, setShowFullConfigPreference] = useState(
      !isSyncForm && !sync?.sync_template,
    );
    const shouldShowFullConfiguration = showFullConfigPreference;

    const {
      error: formkitDefinitionError,
      data,
      isLoading: formkitDefinitionLoading,
    } = useFormkitSyncDefinitionQuery(
      { type: destination?.type ?? "" },
      {
        enabled: Boolean(destination?.type) && !deprecatedForm,
      },
    );

    const { data: destinationConfig, isLoading: destinationConfigLoading } =
      useDestinationQuery(
        { id: String(destination?.id) },
        {
          enabled: Boolean(destination?.id),
          select: (data) => data.destinations_by_pk?.config,
        },
      );

    const { draft: draftModel, mergeResourceWithDraft } = useDraftMerger({
      resourceId: model?.id ?? "",
      resourceType: "model",
    });

    const { data: migrateData } = useMigrateConfigQuery(
      { draftId: draft?.id ?? "" },
      {
        enabled:
          draft?.operation !== DraftOperation.Create && Boolean(draft?.id),
      },
    );

    const baseModel = useMemo(
      () => (model && draftModel ? mergeResourceWithDraft(model) : model),
      [model, draftModel, mergeResourceWithDraft],
    );

    const {
      data: entryAudienceSyncableColumns,
      isLoading: isEntryAudienceSyncableColumnsLoading,
    } = useSyncableColumnsQuery(
      {
        id: journey?.entryAudienceId ?? "",
      },
      {
        enabled: Boolean(
          journey?.entryAudienceId &&
            journey.entryAudienceId.toString() !== baseModel?.id?.toString(),
        ),
        select: selectEntryAudienceSyncableColumns,
      },
    );

    const { journeyColumns, isLoading: isJourneyColumnsLoading } =
      useJourneySyncableColumns(
        journey?.journey_id,
        journey?.entryEventRelationshipId,
      );

    const draftConfig = migrateData?.migrateConfig;

    const formkitDefinition = data?.formkitSyncDefinition.form;
    const templateConfig = syncTemplate?.config ?? sync?.sync_template?.config;
    const templateOverrideConfig =
      syncTemplate?.override_config ?? sync?.sync_template?.override_config;

    const templateHasEmptyFields =
      templateConfig && hasEmptyRequiredFields(templateConfig);
    // Check if template has unlocked fields in addition to empty fields
    const templateHasUnlockedFields = Boolean(
      templateOverrideConfig && hasUnlockedFields(templateOverrideConfig),
    );

    const supportsOverrides =
      // Gate on isSyncForm in this case, because having empty fields
      // should only imply override support only when creating a sync, not
      // when creating a sync template.
      (isSyncForm &&
        journeysSyncTemplates &&
        (syncRequiresCompletion || templateHasEmptyFields)) ||
      templateHasUnlockedFields ||
      Boolean(data?.formkitSyncDefinition.supportsOverrides);

    // Memoize the merged model separately to prevent context recreation
    const mergedModel = useMemo(
      () =>
        mergeJourneySyncModel(
          baseModel,
          entryAudienceSyncableColumns,
          journeyColumns,
        ),
      [baseModel, entryAudienceSyncableColumns, journeyColumns],
    );

    const hasOverrideConfigWithOverridableFields = useMemo(
      () =>
        !isEmpty(templateOverrideConfig) &&
        // If any of the override_config values are explicitly marked as overridable
        Object.values(templateOverrideConfig).some(
          (overrideConfigValue) =>
            (overrideConfigValue as { overridable: boolean })["overridable"] ===
            true,
        ),
      [templateOverrideConfig],
    );

    // Memoize draft changes separately to prevent context recreation
    const memoizedDraftChanges = useMemo(() => {
      if (
        editingDraft &&
        draft?.operation === DraftOperation.Update &&
        draftConfig
      ) {
        return compare(syncConfig || sync?.config, draftConfig)
          .map((o) => {
            if (o.op === "add" || o.op === "replace") {
              return {
                key: o.path.split("/").filter(Boolean).join("."),
                op: o.op,
              };
            } else return null;
          })
          .filter<DraftChange>((v): v is DraftChange => Boolean(v));
      }
      return [];
    }, [editingDraft, draft?.operation, draftConfig, syncConfig, sync?.config]);

    const context: DestinationFormContext = useMemo(() => {
      // Determine journey settings source:
      // 1. For sync templates: use sync template's journey_settings column
      // OR
      // 2. the current (Journey) sync node value that's passed in. We treat that as an "override"
      let derivedJourneySettings = journeySettings;

      const templateJourneySettings =
        syncTemplate?.journey_settings || sync?.sync_template?.journey_settings;

      if (templateJourneySettings) {
        // Note, journeySettings comes from the individual sync within a Journey's sync-node. The specific
        // settings there should take precedence over the template settings.
        derivedJourneySettings = {
          ...templateJourneySettings,
          ...journeySettings,
        };
      }

      return {
        sync,
        matchBoosterRegionControl,
        selectedMatchBoosterRegion,
        hasDependentSyncRun,
        isSyncForm,
        syncConfig: syncConfig || sync?.config,
        // provide the template so that the sync may know the original config values
        // the syncConfig will have the merged values
        syncTemplate: syncTemplate ?? sync?.sync_template ?? undefined,
        relatedSyncsWithOverrides,
        shouldShowFullConfiguration,
        syncRequiresCompletion,
        isUsingSyncTemplate: Boolean(sync?.sync_template || syncTemplate),
        supportsOverrides,
        hasOverrideConfig: hasOverrideConfigWithOverridableFields,
        slug,
        model: mergedModel ?? undefined,
        destination,
        destinationConfig,
        destinationDefinition,
        sourceDefinition,
        externalSegment,
        journeySettings: derivedJourneySettings,
        isModelDraft: Boolean(draftModel),
        draftChanges: memoizedDraftChanges,
      };
    }, [
      sync,
      matchBoosterRegionControl,
      selectedMatchBoosterRegion,
      hasDependentSyncRun,
      shouldShowFullConfiguration,
      slug,
      mergedModel,
      draftModel,
      destination,
      destinationConfig,
      destinationDefinition,
      sourceDefinition,
      externalSegment,
      syncTemplate,
      syncConfig,
      isSyncForm,
      supportsOverrides,
      syncRequiresCompletion,
      journeySettings,
      memoizedDraftChanges,
      relatedSyncsWithOverrides,
      hasOverrideConfigWithOverridableFields,
    ]);

    if (!destination || !model || !sourceDefinition) {
      return (
        <Text>
          You do not have access to the underlying model, source or destination
          needed to edit this configuration.
        </Text>
      );
    }

    // XXX: added `!deprecatedForm` here so we dont have to re-render when loading.
    // Re-rendering when loading is causing the deprecated form to loose state.
    if (
      (!deprecatedForm &&
        !formkitDefinitionError &&
        formkitDefinitionLoading) ||
      destinationConfigLoading ||
      isEntryAudienceSyncableColumnsLoading ||
      isJourneyColumnsLoading
    ) {
      return <Spinner size="lg" m="auto" />;
    }

    if (
      context.isSyncForm &&
      context.isUsingSyncTemplate &&
      !supportsOverrides
    ) {
      return null;
    }

    const formSyncConfig =
      editingDraft && draftConfig ? draftConfig : syncConfig || sync?.config;

    return (
      <Column gap={6}>
        {context.supportsOverrides &&
          context.isSyncForm &&
          context.hasOverrideConfig && (
            <Row justify="space-between" mb={-2}>
              <Text fontWeight="medium" size="lg">
                Configuration overrides
              </Text>
              <Row
                sx={{
                  label: { display: "flex", alignItems: "center", gap: 2 },
                }}
              >
                <Text as="label" fontWeight="medium" size="sm">
                  <Switch
                    aria-label="Show full configuration."
                    size="sm"
                    isChecked={shouldShowFullConfiguration}
                    onChange={setShowFullConfigPreference}
                  />
                  Show full configuration
                </Text>
              </Row>
            </Row>
          )}
        <DestForm
          ref={ref}
          isDisabled={isDisabled}
          context={context}
          deprecatedForm={deprecatedForm}
          formkitDefinition={formkitDefinition}
          hideSave={hideSave}
          hideHeader={hideHeader}
          syncConfig={formSyncConfig}
          onSubmit={onSubmit}
          permission={permission}
          testPermission={testPermission}
          disableRowTesting={disableRowTesting}
          validateOnMount={validateOnMount}
          onLogConfigChange={onLogConfigChange}
        />
      </Column>
    );
  },
);

const NullComponent = () => null;

// Stable module-scope reference for the "everything disabled" branch of
// `isFieldDisabled`. Inlining `() => true` would allocate a fresh function each
// render and destabilize FormkitContext's value memo for every consumer.
const ALWAYS_DISABLED: (key: string) => boolean = () => true;

interface DestFormProps {
  disableRowTesting?: boolean;
  syncConfig: Record<string, unknown>;
  context: DestinationFormContext;
  formkitDefinition: FormkitDefinition;
  deprecatedForm: { form: FC; validation: Yup.ObjectSchema };
  isDisabled?: boolean;
  onSubmit: (config: any) => Promise<void>;
  hideSave: boolean | undefined;
  hideHeader: boolean;
  validateOnMount?: boolean;
  permission:
    | ResourcePermissionInput<"model">
    | ResourcePermissionInput<"sync">;
  testPermission:
    | ResourcePermissionInput<"model">
    | ResourcePermissionInput<"sync">;
  onLogConfigChange?: (
    config: WizardSyncConfig | undefined,
    message: string,
  ) => void;
}

// Memoized so parent query resolutions don't cascade into the form tree —
// rendering it is expensive (every section/select/mapper in one pass).
// All props must stay referentially stable.
const DestForm = memo(
  forwardRef<FormRef, DestFormProps>(function DestForm(
    {
      syncConfig,
      disableRowTesting = false,
      isDisabled = false,
      context,
      formkitDefinition,
      deprecatedForm,
      onSubmit,
      hideSave,
      hideHeader,
      validateOnMount = false,
      permission,
      testPermission,
      onLogConfigChange,
    },
    ref,
  ) {
    const client = useQueryClient();
    const { workspace } = useUser();
    const featureFlags = useFlags();
    const { journeysSyncTemplates } = featureFlags;
    const {
      data: { entitlements },
    } = useEntitlements(true);
    const { toast } = useToast();

    const [customValidation, setCustomValidation] = useState<{
      validate: (config: any) => Promise<{ yupError?: any; otherError?: any }>;
    }>();
    const [editingJson, setEditingJson] = useState(false);
    const [previewField, setPreviewField] =
      useState<TemplatePreviewField | null>(null);
    const templatePreviewContextValue = useMemo(
      () => ({
        openPreview: setPreviewField,
        isPreviewOpen: previewField !== null,
      }),
      [previewField],
    );
    const [initialConfig, setConfig] = useState<any>(syncConfig || {});
    const [saving, setSaving] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>();
    const [isInitialized, setIsInitialized] = useState(false);
    const [isValidationComplete, setIsValidationComplete] =
      useState(!validateOnMount);

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      isDirty: () => form.formState.isDirty,
    }));

    const form = useForm();

    // We must have the feature flag enabled, be editing a template, AND
    // the workspace must allow for syncs for optional formkit-yup validation.
    const allowOptionalValidation =
      !workspace?.sync_templates_only &&
      journeysSyncTemplates &&
      context.supportsOverrides &&
      !context.isSyncForm;

    const DeprecatedForm = deprecatedForm?.form ?? NullComponent;

    const formkitValidate = useCallback(
      async (config) => {
        const queryVariables = {
          type: context.destination?.type ?? "",
          config,
          context: {
            isUsingSyncTemplate: allowOptionalValidation,
            featureFlags,
            entitlements,
            destinationConfig: context.destinationConfig,
            // The mode picker's rules are journey-mode-partitioned, so a
            // validation run without journeySettings silently applies the
            // non-journey rules instead.
            journeySettings: context.journeySettings,
          },
        };
        const response = await client.fetchQuery({
          queryFn: useFormkitSyncValidationQuery.fetcher(queryVariables),
          queryKey: useFormkitSyncValidationQuery.getKey(queryVariables),
        });

        return response.formkitSyncValidation;
      },
      [
        client,
        context.destination?.type,
        allowOptionalValidation,
        featureFlags,
        entitlements,
        context.destinationConfig,
        context.journeySettings,
      ],
    );

    const overrideConfig: OverrideConfig | null = useMemo(() => {
      const syncTemplate = context.sync?.sync_template ?? context.syncTemplate;

      if (context.isSyncForm && context.isUsingSyncTemplate) {
        return getOverrideConfigIncludingEmptyFields(syncTemplate);
      }

      return syncTemplate?.override_config ?? null;
    }, [
      context.isSyncForm,
      context.isUsingSyncTemplate,
      context.sync?.sync_template,
      context.syncTemplate,
    ]);

    const [relationshipHierarchy, setRelationshipHierarchy] =
      useState<RelationshipHierarchy | null>(() =>
        getRelationshipHierarchy({
          supportsOverrides: context.supportsOverrides,
          formkitDefinition,
          isUsingSyncTemplate: context.isUsingSyncTemplate,
        }),
      );

    const addMissingRelationships = useCallback(
      (parent: string, keys: string[]): void => {
        setRelationshipHierarchy((previousHierarchy) => {
          if (!previousHierarchy) {
            return null;
          }

          return addMissingRelationshipToHierarchy({
            hierarchy: previousHierarchy,
            parent,
            keys,
          });
        });
      },
      [],
    );

    // Keep relationship hierarchy in sync when the formkit definition is fetched.
    // Once it's set, don't replace it because new fields may have been added.
    useEffect(() => {
      if (formkitDefinition && !relationshipHierarchy) {
        setRelationshipHierarchy(
          getRelationshipHierarchy({
            supportsOverrides: context.supportsOverrides,
            formkitDefinition,
            isUsingSyncTemplate: context.isUsingSyncTemplate,
          }),
        );
      }
    }, [
      context.supportsOverrides,
      context.isUsingSyncTemplate,
      formkitDefinition,
      relationshipHierarchy,
    ]);

    // Enhance form definition with journey settings for sync templates
    const enhancedFormkitDefinition = useMemo(() => {
      if (!formkitDefinition) {
        return null;
      }

      // Add journey settings node ONLY when editing sync templates (not regular syncs)
      // Regular syncs should handle journey_settings in Journey's sync-config-form.tsx instead
      if (
        !context.isSyncForm &&
        context.isUsingSyncTemplate &&
        journeysSyncTemplates &&
        context.syncTemplate // Ensure we're actually editing a sync template
      ) {
        const journeySettingsNode: FormkitNode = {
          type: NodeType.Component,
          component: ComponentType.JourneySettings,
          key: JOURNEY_SETTINGS_CONFIG_KEY,
          props: {
            showModeSelection: true,
            showExitConfig: true,
            modeLabel: "Journey Sync Mode",
            exitConfigLabel: "Journey Exit Configuration",
          },
        };

        // Create enhanced form definition with journey settings
        const enhancedDefinition: FormkitNode = {
          ...formkitDefinition,
          children: [
            ...(formkitDefinition.children || []),
            journeySettingsNode,
          ],
        };

        return enhancedDefinition;
      }

      return formkitDefinition;
    }, [
      formkitDefinition,
      context.isSyncForm,
      context.isUsingSyncTemplate,
      journeysSyncTemplates,
      context.syncTemplate,
    ]);

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    const formkit = useMemo(() => {
      if (!enhancedFormkitDefinition) {
        return null;
      }

      const canPartialFormBeShown = Boolean(
        context.isSyncForm &&
          overrideConfig &&
          hasUnlockedFields(overrideConfig),
      );

      const clearFieldsFunction = (nodeKeys: string[]) => {
        nodeKeys.forEach((key) => {
          // Only clear if the key exists in the form
          if (form.getFieldState(key)) {
            // Clear the parent field based on its original type
            const currentValue = form.getValues(key);
            const emptyValue = getEmptyValue(currentValue);
            form.setValue(key, emptyValue, { shouldDirty: true });
            // Clear all child fields recursively
            const childFields = getAllChildFields(key, relationshipHierarchy);
            childFields.forEach((childKey) => {
              if (form.getFieldState(childKey)) {
                const childValue = form.getValues(childKey);
                const childEmptyValue = getEmptyValue(childValue);
                form.setValue(childKey, childEmptyValue, { shouldDirty: true });
              }
            });
          }
        });
      };

      const showClearable =
        journeysSyncTemplates &&
        context.supportsOverrides &&
        !context.isSyncForm &&
        Boolean(context.syncTemplate);
      const syncTemplateOverrideContext = context.supportsOverrides
        ? {
            showLocks:
              !context.isSyncForm && Boolean(context.isUsingSyncTemplate),
            showOverrides: canPartialFormBeShown,
            showClearable,
            clearFields: clearFieldsFunction,
            overrideConfig,
            isNodeVisible: (node: FormkitNode) => {
              // For sync templates (not sync forms), show all configuration
              if (context.shouldShowFullConfiguration || !context.isSyncForm) {
                return true;
              }
              // For syncs from templates, use template-based visibility
              if (context.isUsingSyncTemplate && context.syncTemplate) {
                return isNodeVisible({
                  node,
                  relationshipHierarchy,
                  overrideConfig,
                  // If journeysSyncTemplates is not enabled, we should not pass in the templateConfig. This means
                  // we would ignore the check if a field is empty on the template.
                  templateConfig: journeysSyncTemplates
                    ? context.syncTemplate?.config
                    : null,
                });
              }

              // For regular syncs, show all fields
              return true;
            },
          }
        : {
            showClearable,
            clearFields: clearFieldsFunction,
          };

      return (
        <ProcessFormNode
          node={enhancedFormkitDefinition}
          context={{
            ...context,
            ...syncTemplateOverrideContext,
          }}
        />
      );
    }, [
      enhancedFormkitDefinition,
      context,
      relationshipHierarchy,
      overrideConfig,
      journeysSyncTemplates,
    ]);

    // XXX: Existing state should be set via reset without setting the default values
    // This is because default values should be set by the components only
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      if (syncConfig && Object.keys(syncConfig).length && !isInitialized) {
        const newConfig = { ...syncConfig };

        // Add locking for sync templates
        if (context.supportsOverrides) {
          if (!context.isSyncForm) {
            newConfig[OVERRIDE_CONFIG_FORM_KEY] =
              context.syncTemplate?.override_config ?? null;
          }
        }

        form.reset(newConfig, {
          keepDefaultValues: true,
        });

        // TODO(samuel): Deprecated forms won't handle sync template overrides
        setConfig(syncConfig || {});
      }
      setIsInitialized(true);
    }, [
      context.supportsOverrides,
      context.isSyncForm,
      context.syncTemplate?.override_config,
      syncConfig,
      isInitialized,
    ]);

    // Validate the form on mount if the validateOnMount flag is set to true
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      if (validateOnMount && isInitialized && formkitDefinition) {
        const runInitialValidation = async () => {
          try {
            const currentConfig = form.getValues();
            const cleanedConfig = cleanConfig(currentConfig);
            const errors = await formkitValidate(cleanedConfig);

            if (typeof errors === "object" && Object.keys(errors).length) {
              // Set errors on form fields for inline display
              Object.entries(errors).forEach(([key, message]) => {
                form.setError(key, { message: String(message) });
              });
              // Store errors in state for analytics/tracking
              setErrors(errors);
            }
          } catch {
            // If this fails, we should still set the validation complete flag to true
            // so that the form can be rendered.
            // Form still works if validaiton on mount fails.
          } finally {
            setIsValidationComplete(true);
          }
        };

        void runInitialValidation();
      }
    }, [validateOnMount, isInitialized, formkitDefinition]);

    const config = formkitDefinition
      ? form.getValues()
      : deprecatedForm?.validation?.cast(initialConfig, { assert: false });

    const orphanedKeys = enhancedFormkitDefinition
      ? getOrphanedConfigKeys(form.getValues(), enhancedFormkitDefinition)
      : [];

    // Cleanup is only offered on regular syncs not driven by a template.
    // Templates manage their own keys through override and locking logic.
    const showOrphanedKeysSection =
      orphanedKeys.length > 0 &&
      context.isSyncForm &&
      !context.isUsingSyncTemplate;

    const handleClearOrphanedKeys = (keysToRemove: string[]) => {
      // Unregister scrubs each key from both form values and default values.
      // A reset with omitted values leaves defaults behind and getValues() merges them back.
      for (const key of keysToRemove) {
        form.register(key);
      }
      form.unregister(keysToRemove);
      toast({
        id: "clear-orphaned-keys",
        title: `Removed ${pluralize("key", keysToRemove.length, true)}`,
        message: "Save to apply.",
        variant: "success",
      });
    };

    // Stable identity matters: `validate` feeds FormkitProvider's context value
    // memo, so a fresh closure per render re-renders every mounted FKComponent.
    const validate = useCallback(
      async (config) => {
        if (formkitDefinition) {
          const cleanedConfig = cleanConfig(config);
          const errors = await formkitValidate(cleanedConfig);
          if (typeof errors === "object" && Object.keys(errors).length) {
            return errors;
          }
        } else {
          return oldValidate(
            config,
            deprecatedForm.validation,
            customValidation,
          );
        }
      },
      [formkitDefinition, formkitValidate, deprecatedForm, customValidation],
    );

    const processSyncOverrides = (
      cleanedConfig: Record<string, unknown>,
      data: Record<string, unknown>,
    ) => {
      const usedEnteredValues = cleanConfig(data);
      // Get the fields that are unlocked
      // The override config stores all the fields that are unlocked
      // Need to get the keys of the fields that are unlocked and their children!
      const unlockedFields = getUnlockedFields(
        overrideConfig,
        relationshipHierarchy,
      );
      const newValues = {};

      // If there are no unlocked fields, but we still have form data that differs from template,
      // we should still capture those as overrides (this handles cases where override config isn't properly set)
      if (unlockedFields.length === 0 && context.syncTemplate?.config) {
        // Compare all form fields against template defaults and capture differences
        Object.keys(cleanedConfig).forEach((key) => {
          const defaultValue = get(context.syncTemplate?.config, key);
          const newValue = get(cleanedConfig, key);
          const existingOverrideValue = get(
            context.sync?.sync_template_overrides,
            key,
          );

          // Include if different from template default or has existing override
          if (
            !isEqual(newValue, defaultValue) ||
            existingOverrideValue !== undefined
          ) {
            newValues[key] = newValue;
          }
        });
      } else {
        if (context.syncRequiresCompletion && context.syncTemplate?.config) {
          Object.keys(cleanedConfig).forEach((key) => {
            const defaultValue = get(context.syncTemplate?.config, key);
            const newValue = get(cleanedConfig, key);
            const existingOverrideValue = get(
              context.sync?.sync_template_overrides,
              key,
            );

            if (
              !isEqual(newValue, defaultValue) ||
              existingOverrideValue !== undefined
            ) {
              newValues[key] = newValue;
            }
          });
        }

        // Standard logic for unlocked fields
        // Pull the updated config values from the config object
        // if they have changed from the initial config
        // Only send updated values, not the entire config
        unlockedFields.forEach((key) => {
          const defaultValue = get(context.syncTemplate?.config, key);
          const existingOverrideValue = get(
            context.sync?.sync_template_overrides,
            key,
          );
          const newValue = get(cleanedConfig, key);

          if (
            !isEqual(newValue, defaultValue) ||
            existingOverrideValue !== undefined
          ) {
            newValues[key] = newValue;
          }
        });
      }

      // For templates with empty required fields, ensure we include values for those fields
      // even if they weren't originally unlocked
      if (
        context.syncTemplate?.config &&
        hasEmptyRequiredFields(context.syncTemplate.config)
      ) {
        for (const [key, templateValue] of Object.entries(
          context.syncTemplate.config,
        )) {
          if (templateValue === null || templateValue === undefined) {
            const providedValue = get(usedEnteredValues, key);
            // Only include if a value was actually provided
            if (
              providedValue !== undefined &&
              providedValue !== null &&
              providedValue !== ""
            ) {
              newValues[key] = providedValue;
            }
          }
        }
      }

      return newValues;
    };

    const processSyncTemplateOverrideConfig = (
      cleanedConfig: Record<string, unknown>,
      __overrideConfig: OverrideConfig,
    ) => {
      // When editing a template, we need to preserve override fields that exist in dependent syncs
      // even if those fields are no longer in the template config
      const configKeys = Object.keys(cleanedConfig);
      const dependentSyncOverrideKeys = Object.keys(
        context.relatedSyncsWithOverrides || {},
      );

      // Also include any keys that are marked as editable in the current override config
      const editableKeys = __overrideConfig
        ? Object.keys(__overrideConfig).filter(
            (key) => __overrideConfig[key]?.overridable,
          )
        : [];

      // Apply overrideConfig to children nodes by default - include children of editable fields
      const editableKeysWithChildren: string[] = [];
      editableKeys.forEach((key) => {
        editableKeysWithChildren.push(key);
        editableKeysWithChildren.push(
          ...getAllChildFields(key, relationshipHierarchy),
        );
      });
      // Include current config keys, dependent sync override keys, AND editable keys with their children
      const allRelevantKeys = [
        ...new Set([
          ...configKeys,
          ...dependentSyncOverrideKeys,
          ...editableKeysWithChildren,
        ]),
      ];
      // Create enhanced override config that includes children of overridable fields
      const enhancedOverrideConfig = { ...__overrideConfig };
      allRelevantKeys.forEach((key) => {
        const overrideConfigValue = enhancedOverrideConfig[key];
        const { isAnyAncestorUnlocked, fieldIsEmpty } = getFieldContext({
          key,
          overrideConfig,
          relationshipHierarchy,
          templateConfig: enhancedOverrideConfig,
        });
        enhancedOverrideConfig[key] = {
          overridable:
            overrideConfigValue?.overridable !== undefined
              ? // Set overridable if field is present as overridable in __overrideConfig
                overrideConfigValue?.overridable
              : // Otherwise set to false if field is currently empty AND none of ancestor fields are unlocked.
                // [2025-09-19] We're making this assumption because if the field is empty and everything upstream
                // is locked on the template, we shouldn't be able to edit this node/field.
                fieldIsEmpty && isAnyAncestorUnlocked,
        };
      });

      return cleanOverrides(
        enhancedOverrideConfig as OverrideConfig | null,
        allRelevantKeys,
        {
          onlyUnlocked: !journeysSyncTemplates,
        },
      );
    };

    const handleSyncSubmission = async (
      data: Record<string, unknown>,
      cleanedConfig: Record<string, unknown>,
    ) => {
      let values = { ...cleanedConfig };
      if (
        context.supportsOverrides &&
        context.isUsingSyncTemplate &&
        context.syncTemplate?.config
      ) {
        const overrides = processSyncOverrides(cleanedConfig, data);
        // For template-based syncs, return template base config + overrides separately
        values = {
          // We do not need to call cleanConfig here because syncTemplate.config was
          // already cleaned + persisted in the DB.
          ...context.syncTemplate.config, // Use template base config
          sync_template_overrides: overrides, // Store user overrides separately
        };
      }
      onLogConfigChange?.(values, "submitting new values");
      await onSubmit(values);
      setErrors(null);
    };

    const handleSyncTemplateSubmission = async (
      cleanedConfig: Record<string, unknown>,
      __overrideConfig: OverrideConfig,
    ) => {
      const values = { ...cleanedConfig };

      if (context.supportsOverrides && context.isUsingSyncTemplate) {
        values["overrideConfig"] = processSyncTemplateOverrideConfig(
          cleanedConfig,
          __overrideConfig,
        );
      }
      onLogConfigChange?.(values, "[template] submitting new values");
      await onSubmit(values);
      setErrors(null);
    };

    const handleFormkitSubmission = async (formData: any) => {
      onLogConfigChange?.(formData, "begin formkit submission");
      const { __overrideConfig, ...data } = formData;
      const deepClonedOverrideConfig = cloneDeep(__overrideConfig);
      const deepClonedData = cloneDeep(data);

      const cleanedConfig = cleanConfig(deepClonedData);
      onLogConfigChange?.(formData, "config cleaned");

      // Validate Liquid templates against v10 parser before saving. Runs for
      // every workspace so new saves can't introduce templates that the v10
      // engine will reject once the runtime rollout flips on.
      const templateErrors = validateSyncConfigTemplates(cleanedConfig);
      const errors = {
        ...templateErrors,
        ...(await formkitValidate(cleanedConfig)),
      };

      if (typeof errors === "object" && Object.keys(errors).length) {
        const errorMessages: string[] = [];
        Object.entries(errors).forEach(([key, message]) => {
          form.setError(key, { message: String(message) });
          errorMessages.push(String(message));
        });
        analytics.track("Destination Config Validation Error");
        toast({
          id: "save-sync-config",
          title: "Couldn't save the sync configuration",
          message: `There were ${pluralize("error", errorMessages.length, true)}: ${errorMessages.join(", ")}`,
          variant: "error",
        });

        setErrors(errors);
        return;
      }
      if (context.isSyncForm) {
        await handleSyncSubmission(deepClonedData, cleanedConfig);
      } else {
        await handleSyncTemplateSubmission(
          cleanedConfig,
          deepClonedOverrideConfig,
        );
      }
    };

    const handleDeprecatedSubmission = async () => {
      const errors = await oldValidate(
        config,
        deprecatedForm.validation,
        customValidation,
      );

      if (errors) {
        analytics.track("Destination Config Validation Error");

        toast({
          id: "save-sync-config",
          title: "Couldn't save the sync configuration",
          variant: "error",
        });

        setErrors(errors);
        return;
      }

      onLogConfigChange?.(config, "deprecated sync form submission");
      await onSubmit(config);
      setErrors(null);
    };

    const handleSubmit = async () => {
      setSaving(true);

      try {
        // Validate templates against the Liquid parser (after the same v9→v10
        // compatibility transform the runtime applies). Blocks save if any
        // template has syntax the engine rejects.
        {
          const currentConfig = form.getValues();
          const templateErrors = validateSyncConfigTemplates({
            ...initialConfig,
            ...currentConfig,
          } as Record<string, unknown>);
          if (Object.keys(templateErrors).length > 0) {
            const [field, message] = Object.entries(templateErrors)[0]!;
            toast({
              id: "liquid-v10-validation",
              title: `Template "${field}" is not compatible with Liquid v10`,
              message,
              variant: "error",
            });
            setSaving(false);
            return;
          }
        }

        if (formkitDefinition) {
          // XXX: temporary fix to clear form errors before submit handler is validated so that the handleSubmit can
          // re-validate the form when theres an error. The proper fix should be to refactor the code to only use
          // "setErrors" only and not a combination of "setErrors" and "useForm.setError" within the formkit code
          form.clearErrors();
          await form.handleSubmit(handleFormkitSubmission)();
        } else {
          await handleDeprecatedSubmission();
        }
      } catch (_) {
        toast({
          id: "save-sync-config-error",
          title: "An error occurred while saving",
          variant: "error",
        });
      } finally {
        setSaving(false);
      }
    };

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      if (editingJson) {
        analytics.track("Sync JSON Editor Opened", {
          sync_id: context.sync?.id,
          sync_template_id: context.sync?.sync_template_id,
          sync_slug: context.slug,
          model_name: context.model?.name,
          query_type: context.model?.query_type,
          destination_type: context.destination?.type,
          source_type: context.sourceDefinition?.type,
        });
      }
    }, [editingJson]);

    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
    useEffect(() => {
      if (errors) {
        analytics.track("Sync Destination Configuration Error", {
          sync_id: context.sync?.id,
          sync_slug: context.slug,
          model_name: context.model?.name,
          query_type: context.model?.query_type,
          destination_type: context.destination?.type,
          source_type: context.sourceDefinition?.type,
          errors,
        });
      }
    }, [errors]);

    const { data: supportsMatchboosting } = useSupportsMatchboostingQuery(
      {
        config,
        destinationType: context.destination?.type ?? "",
      },
      {
        enabled: !!context.destination?.slug,
        keepPreviousData: true,
      },
    );

    const isFieldDisabledForOverrides = useCallback(
      (key: string): boolean => {
        const isExplicitlyLocked =
          get(overrideConfig, [key, "overridable"]) === false;
        if (isExplicitlyLocked) {
          return true;
        }
        const {
          isUnlocked,
          isAnyAncestorUnlocked,
          fieldIsEmpty,
          isAnyAncestorEmpty,
        } = getFieldContext({
          key,
          overrideConfig,
          relationshipHierarchy,
          templateConfig: context.syncTemplate?.config,
        });

        const tc = context.syncTemplate?.config;
        const fieldIsExplicitlyEmpty = tc && key in tc && fieldIsEmpty;
        if (!isUnlocked && !fieldIsExplicitlyEmpty) {
          // If there's currently a value and the field not explicitly marked as editable.
          return true;
        }
        // The field is disabled if it's not editable (unlocked) AND not empty
        // Field must be editable OR empty to be configurable on the sync
        return (
          !isUnlocked &&
          !isAnyAncestorUnlocked &&
          !fieldIsEmpty &&
          !isAnyAncestorEmpty
        );
      },
      [overrideConfig, relationshipHierarchy, context.syncTemplate?.config],
    );

    const isFieldDisabled = useMemo<
      ((key: string) => boolean) | undefined
    >(() => {
      if (isDisabled) {
        return ALWAYS_DISABLED;
      }
      if (
        context.supportsOverrides &&
        context.isSyncForm &&
        context.hasOverrideConfig
      ) {
        return isFieldDisabledForOverrides;
      }
      return undefined;
    }, [
      isDisabled,
      context.supportsOverrides,
      context.isSyncForm,
      context.hasOverrideConfig,
      isFieldDisabledForOverrides,
    ]);

    if (!isInitialized || !isValidationComplete) {
      return null;
    }

    return (
      <FormkitProvider
        {...context}
        onAddMissingRelationships={addMissingRelationships}
        isFieldDisabled={isFieldDisabled}
        showRestoreDefaultUI={
          context.supportsOverrides &&
          context.hasOverrideConfig &&
          context.isSyncForm &&
          hasUnlockedFields(overrideConfig)
        }
        showLockUI={
          context.supportsOverrides &&
          context.isUsingSyncTemplate &&
          !context.isSyncForm
        }
        relationshipHierarchy={
          context.supportsOverrides ? relationshipHierarchy : undefined
        }
        relatedSyncsWithOverrides={context.relatedSyncsWithOverrides}
        validate={validate}
        supportsMatchboosting={supportsMatchboosting?.supportsMatchboosting}
      >
        <DestinationFormProvider
          config={config}
          errors={errors}
          setConfig={setConfig}
          setCustomValidation={setCustomValidation}
          setErrors={setErrors}
        >
          <TemplatePreviewContext.Provider value={templatePreviewContextValue}>
            <FormProvider {...form}>
              {!hideHeader && (
                <Row align="center" justify="space-between">
                  <Row gap={1} overflow="hidden">
                    <IntegrationIcon
                      name={context.destinationDefinition.name}
                      src={context.destinationDefinition.icon}
                      size={6}
                    />
                    <TextWithTooltip
                      message={`Sync configuration to ${context.destinationDefinition.name}`}
                      size="lg"
                      fontWeight="medium"
                    >
                      <>
                        Sync configuration to{" "}
                        {context.destinationDefinition.name}
                      </>
                    </TextWithTooltip>
                  </Row>
                  <Row align="center" gap={4} overflow="hidden">
                    <DocsLink
                      name={context.destinationDefinition.name}
                      href={context.destinationDefinition.docs}
                    />
                    {!disableRowTesting && (
                      <TestSync formkit={formkit} permission={testPermission} />
                    )}
                    {
                      // Do not show the Edit as JSON button if we're using a template.
                      !(
                        context.isSyncForm &&
                        (context.sync?.sync_template || context.syncTemplate)
                      ) && (
                        <Box flexShrink={0}>
                          <PermissionedButton
                            isDisabled={isDisabled}
                            permission={permission}
                            onClick={() => {
                              setEditingJson(true);
                            }}
                          >
                            Edit as JSON
                          </PermissionedButton>
                        </Box>
                      )
                    }
                  </Row>
                </Row>
              )}
              <Column flex={1} minWidth={0}>
                <Form>
                  {enhancedFormkitDefinition ? formkit : <DeprecatedForm />}
                  {showOrphanedKeysSection && (
                    <OrphanedConfigKeysSection
                      orphanedKeys={orphanedKeys}
                      config={form.getValues()}
                      onClear={handleClearOrphanedKeys}
                      isDisabled={isDisabled}
                      permission={permission}
                    />
                  )}
                </Form>
                <form
                  hidden
                  id="destination-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void handleSubmit();
                  }}
                ></form>
              </Column>
              {editingJson && (
                <JsonEditor
                  keysToOmit={[
                    OVERRIDE_CONFIG_FORM_KEY,
                    JOURNEY_SETTINGS_CONFIG_KEY,
                  ]}
                  formkit={Boolean(enhancedFormkitDefinition)}
                  onClose={() => {
                    setEditingJson(false);
                  }}
                />
              )}
              {previewField && (
                <TemplatePreviewModalWrapper
                  field={previewField}
                  onClose={() => setPreviewField(null)}
                />
              )}
              {!hideSave && (
                <FloatingActionBar>
                  <PermissionedButton
                    //We shouldn't rely on dirty state for isDisabled because the logic is broken
                    size="lg"
                    variant="primary"
                    permission={permission}
                    isLoading={saving}
                    onClick={handleSubmit}
                  >
                    {context.isSyncForm && workspace?.approvals_required
                      ? "Save draft"
                      : "Save changes"}
                  </PermissionedButton>
                </FloatingActionBar>
              )}
            </FormProvider>
          </TemplatePreviewContext.Provider>
        </DestinationFormProvider>
      </FormkitProvider>
    );
  }),
);

/**
 * Sample-data + trait-row-key fetching for the template Test modal. React Query
 * dedupes the row-key request by key, so re-opening the modal is cheap.
 */
function useTemplatePreviewData() {
  const { model, sourceDefinition } = useFormkitContext();
  const { rows, reloadRows, loadingRows } = useFormkitDataContext();

  const { data: rowKeyData, isLoading: loadingRowKeys } =
    useListSyncableColumnRowKeysQuery(
      { modelId: model?.id?.toString() ?? "" },
      {
        enabled: model?.id != null,
        select: (data) => data.listSyncableColumnRowKeys,
      },
    );

  const traitRowKeys: Record<string, string> = {};
  for (const entry of rowKeyData ?? []) {
    traitRowKeys[entry.reference] = entry.rowKey;
  }

  return {
    rows: rows as Record<string, unknown>[] | undefined,
    reloadRows,
    loadingRows,
    loadingRowKeys,
    model,
    sourceDefinition,
    traitRowKeys,
  };
}

function TemplatePreviewModalWrapper({
  field,
  onClose,
}: {
  field: TemplatePreviewField;
  onClose: () => void;
}) {
  const {
    rows,
    reloadRows,
    loadingRows,
    loadingRowKeys,
    model,
    sourceDefinition,
    traitRowKeys,
  } = useTemplatePreviewData();

  return (
    <TemplatePreviewModal
      onClose={onClose}
      onApply={(newTemplate) => {
        field.applyTemplate(newTemplate);
      }}
      rows={rows}
      loadingRows={loadingRows}
      loadingRowKeys={loadingRowKeys}
      modelName={model?.name}
      sourceIcon={sourceDefinition?.icon}
      sourceName={sourceDefinition?.name}
      onLoadRows={reloadRows}
      templateFields={[{ to: field.to, template: field.template }]}
      traitRowKeys={traitRowKeys}
    />
  );
}
